import api from '../../../../api';
import { AesDecrypt, AesEncrypt, keyPromise } from '../../../../Service/crypto';
import { startBotRuntimeAutomation, startRpaRuntimeAutomation, stopBotRuntimeAutomation, stopRpaRuntimeAutomation } from '../../../../Service/botRuntimeService';
import { log } from 'console';

// --- Shared Interfaces ---

export type LoopStepsMap = Record<string, Array<{ step: string; value: string }>>;

export interface BotManagerTask {
  id: string;
  project_title: string;
  Domain?: string;
  Description?: string;
  bot_status?: string;
  RPA_status?: string;
  createdAt?: string;
  steps?: any[] | Record<string, any>;
  Dataset?: Record<string, any> | any[];
  Task_Type?: string;
  credentialsReusable?: 'Yes' | 'No' | '';
  instruction?: string;
  template?: string;
  templateId?: string;
  templateName?: string;
  ClientId?: string;
  ClientName?: string;
  session_id?: string;
  multiple?: string[];
  Username?: string;
  Flow_error_details?: {
    expected_value?: string;
    field_name?: string;
    message?: string;
    s3_url?: string;
    step?: number;
    timestamp?: string;
  };
  loop_steps?: number[] | LoopStepsMap;
}


export interface ActiveBotTask {
  id: string;
  project_title: string;
  Domain?: string;
  Description?: string;
  bot_status?: string;
  created_date?: string;
  steps?: any[];
  Username?: string;
}

export interface ConvertToRpaResponse {
  success: boolean;
  message?: string;
  prompt_required?: boolean;
  data?: any;
}

export interface BotManagerAuditLog {
  id: string;
  Action: string;
  JsonData: any;
  UserName: string;
  Modified: string;
}

export interface FetchBotTasksParams {
  q?: string;
  perPage?: number;
  page?: number;
  sort?: 'asc' | 'desc' | '';
  sortColumn?: string;
  filterUserId?: string;
  filterBotStatus?: string;
}

// --- Helper ---

const encryptedPost = async <T,>(url: string, payload: unknown): Promise<T> => {
  const key = await keyPromise;
  // Deep clone to ensure sibling elements get unique object references in memory
  const cleanPayload = JSON.parse(JSON.stringify(payload));
  // Add circular reference replacer to prevent stack overflow with large datasets
  const seen = new WeakSet();
  const replacer = (key: string, value: any) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) {
        return '[Circular]';
      }
      seen.add(value);
    }
    return value;
  };
  const EncParams = await AesEncrypt(key, JSON.stringify(cleanPayload, replacer));
  const response = await api.fetch(url, {
    method: 'POST',
    body: JSON.stringify({ EncParams }),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const raw = await response.json();
  return AesDecrypt(key, raw) as Promise<T>;
};

// --- Bot Manager Functions ---

export const fetchBotTasks = async (params: FetchBotTasksParams) => {
  const payload = {
    userId: localStorage.getItem('id') || '',
    role: localStorage.getItem('Role') || '',
    Usertype: localStorage.getItem('UserType') || '',
    perPage: params.perPage || 10,
    page: params.page || 1,
    sort: params.sort || 'desc',
    sortColumn: params.sortColumn || 'createdAt',
    q: params.q || '',
    filterUserId: params.filterUserId || '',
  };

  const result = await encryptedPost<{ data?: BotManagerTask[]; count?: number } | string>('api/tasks/get-tasks', payload);

  if (typeof result === 'string') {
    return { data: [], count: 0 };
  }

  return {
    data: Array.isArray(result.data) ? result.data : [],
    count: Number(result.count || 0),
  };
};

export const fetchRpaTasks = async (params: FetchBotTasksParams) => {
  const payload = {
    userId: localStorage.getItem('id') || '',
    role: localStorage.getItem('Role') || '',
    Usertype: localStorage.getItem('UserType') || '',
    perPage: params.perPage || 10,
    page: params.page || 1,
    sort: params.sort || 'desc',
    sortColumn: params.sortColumn || 'createdAt',
    q: params.q || '',
    filterUserId: params.filterUserId || '',
    filterBotStatus: params.filterBotStatus || '',
  };

  const result = await encryptedPost<{ data?: BotManagerTask[]; count?: number } | string>('api/rpa-management/get-rpa-list', payload);
  if (typeof result === 'string') {
    return { data: [], count: 0 };
  }

  return {
    data: Array.isArray(result.data) ? result.data : [],
    count: Number(result.count || 0),
  };
};

export const fetchTaskAuditLog = async (id: string) => {
  const result = await encryptedPost<BotManagerAuditLog[]>('api/tasks/auditlog-task', { id });
  // console.log('fetchTaskAuditLog result:', result);
  return Array.isArray(result) ? result : [];
};

export const fetchRpaAuditLog = async (id: string) => {
  const result = await encryptedPost<BotManagerAuditLog[]>('api/rpa-management/auditlog-rpa', { id });
  return Array.isArray(result) ? result : [];
};

export const fetchRpaErrorImage = async (id: string) => {
  return encryptedPost<{ url?: string; data?: any }>('api/rpa-management/error-image', { id });
};

export const updateBotTask = async (payload: {
  id: string;
  project_title: string;
  Domain: string;
  Description: string;
  steps?: any[] | Record<string, any>;
  dataset: Record<string, any> | any[];
  credentialsReusable?: 'Yes' | 'No' | '';
  UserId: string;
  Username: string;
  ClientId?: string;
  ClientName?: string;
  templateId?: string;
  templateName?: string;
  Before?: Record<string, any>;
  After?: Record<string, any>;
}) => encryptedPost<{ success: boolean; message: string }>('api/tasks/update-task', payload);

export const updateRpaTask = async (payload: {
  id: string;
  project_title: string;
  Domain: string;
  Description: string;
  steps?: any[] | Record<string, any>;
  dataset: Record<string, any> | any[];
  credentialsReusable?: 'Yes' | 'No' | '';
  UserId: string;
  Username: string;
  Before?: Record<string, any>;
  After?: Record<string, any>;
  loop_steps?: LoopStepsMap;
  multiple?: string[];
}) => encryptedPost<{ success: boolean; message: string }>('api/rpa-management/update-rpa-task', payload);

export const RPAbulkdataupdate = async (payload: {
  id: string;
  dataset: Record<string, any> | any[];
  UserId: string;
  Username: string;
  multiple?: string[];
  Before?: Record<string, any>;
  After?: Record<string, any>;
}) => {
  // console.log('PAYLOAD', payload)
  return encryptedPost<{ success: boolean; message: string }>('api/rpa-management/update-rpa', payload);
};


export const deleteBotTask = async (payload: {
  id: string;
  name: string;
  deletedBy: string;
}) => encryptedPost<{ success: boolean; message: string }>('api/tasks/delete-task', payload);

export const fetchClients = async (): Promise<{ id: string; CompanyName: string }[]> => {
  const userType = localStorage.getItem('UserType') || '';
  const vendorId = localStorage.getItem('VendorId') || '';
  let bodyData: any = {};
  if (userType === 'MOS') {
    bodyData = { UserType: userType };
  } else if (userType === 'Vendor') {
    bodyData = { UserType: userType, VendorId: vendorId };
  } else {
    bodyData = { UserType: userType };
  }

  const response = await api.fetch('api/Bulkupload/get-client', {
    method: 'POST',
    body: JSON.stringify(bodyData),
  }).then((r) => r.json());

  const key = await keyPromise;
  const decrypted = await AesDecrypt(key, response);
  return decrypted as { id: string; CompanyName: string }[];
};

export const fetchTemplateCodes = async (templateId: string): Promise<any> => {
  return encryptedPost<any>('api/rpa-management/template_codes', { id: templateId });
};

export const fetchTemplates = async (clientId: string): Promise<{ id: string; file_name: string }[]> => {
  const key = await keyPromise;
  const EncParams = await AesEncrypt(key, clientId);
  const response = await api.fetch('api/BulkUpload/template', {
    method: 'POST',
    body: JSON.stringify({ EncParams }),
  }).then((r) => r.json());

  const decrypted = await AesDecrypt(key, response);
  return decrypted as { id: string; file_name: string }[];
};

export const startBotTask = async (payload: {
  userId: string;
  username: string;
  taskId: string;
  title: string;
}) => {

  const result = await encryptedPost<{ success: boolean; message: string }>('api/tasks/start-bot', payload);

  return result;
};

export const stopBotTask = async (payload: {
  userId: string;
  username: string;
  taskId: string;
  title: string;
}) => encryptedPost<{ success: boolean; message: string }>('api/tasks/stop-bot', payload);

export const startRpaTask = async (payload: {
  userId: string;
  username: string;
  rpaId: string;
  title: string;
}) => encryptedPost<{ success: boolean; message: string }>('api/rpa-management/start-bot', payload);

export const stopRpaTask = async (payload: {
  userId: string;
  username: string;
  taskId: string;
  title: string;
}) => encryptedPost<{ success: boolean; message: string }>('api/rpa-management/stop-bot', payload);

export const convertBotTaskToRpa = async (payload: {
  id: string;
  name: string;
  user_id: string;
  user_name: string;
  action?: string;
}) => encryptedPost<ConvertToRpaResponse>('api/rpa-management/convert-to-rpa', payload);

export const deleteRpaTask = async (payload: {
  id: string;
  name: string;
  deletedBy: string;
}) => encryptedPost<{ success: boolean; message: string }>('api/rpa-management/delete-rpa', payload);

export const startBotTaskRuntime = async (payload: {
  userId: string;
  taskId: string;
  dataset?: unknown;
}) => startBotRuntimeAutomation({
  user_id: payload.userId,
  task_id: payload.taskId,
  dataset: payload.dataset,
});

export const stopBotTaskRuntime = async (payload: {
  userId: string;
  taskId: string;
}) => stopBotRuntimeAutomation({
  user_id: payload.userId,
  select_id: payload.taskId,
});

export const startRpaTaskRuntime = async (payload: {
  userId: string;
  taskId: string;
}) => startRpaRuntimeAutomation({
  user_id: payload.userId,
  rpa_id: payload.taskId,
});

export const stopRpaTaskRuntime = async (payload: {
  userId: string;
  taskId: string;
}) => {
  // console.log('🛑 [STORE] stopRpaTaskRuntime called with:', payload);
  try {
    const result = await stopRpaRuntimeAutomation({
      user_id: payload.userId,
      rpa_id: payload.taskId,
    });
    // console.log('✅ [STORE] stopRpaTaskRuntime completed:', result);
    return result;
  } catch (error) {
    console.error('❌ [STORE] stopRpaTaskRuntime failed:', error);
    throw error;
  }
};

export const fetchBotManagerUsers = async () => {
  const result = await encryptedPost<{ value: string; label: string }[]>('api/users-option', {});
  return Array.isArray(result) ? result : [];
};

export const fetchBulkClients = async (payload: { UserType: string; VendorId?: string }) => {
  const endpoint = payload.UserType === 'Vendor' 
    ? 'api/BulkUpload/vendorbasedclientOptions' 
    : 'api/BulkUpload/companyname';
  
  const body = payload.UserType === 'Vendor' ? { id: payload.VendorId } : {};
  const result = await encryptedPost<any[]>(endpoint, body);
  return Array.isArray(result) ? result.map(c => ({
    label: c.CompanyName,
    value: c.id || c.CompanyName
  })) : [];
};

export const fetchBulkTemplates = async (clientId: string) => {
  const result = await encryptedPost<any[]>('api/BulkUpload/template', clientId);
  return Array.isArray(result) ? result.map(t => ({
    label: t.file_name,
    value: t.id
  })) : [];
};

export const fetchUserPermissions = async (userId: string) => {
  const response = await api.fetch('api/Get_permission', {
    method: 'POST',
    body: JSON.stringify({ data: userId }),
  });
  return response.json();
};

export const fetchActiveBotTasks = async (params: {
  q?: string;
  perPage?: number;
  page?: number;
  sort?: 'asc' | 'desc';
  sortColumn?: string;
}) => {
  const payload = {
    userId: localStorage.getItem('id') || '',
    role: localStorage.getItem('Role') || '',
    user_type: localStorage.getItem('UserType') || '',
    perPage: params.perPage || 10,
    page: params.page || 1,
    sort: params.sort || 'desc',
    sortColumn: params.sortColumn || 'createdAt',
    q: params.q || '',
  };

  const result = await encryptedPost<{ data?: ActiveBotTask[]; count?: number } | string>('api/activity/get-activities', payload);

  if (typeof result === 'string') {
    return { data: [], count: 0 };
  }

  return {
    data: Array.isArray(result.data) ? result.data : [],
    count: Number(result.count || 0),
  };
};

export const stopActiveBotTask = async (payload: {
  userId: string;
  username: string;
  taskId: string;
  title: string;
}) => encryptedPost<{ success: boolean; message: string }>('api/tasks/stop-bot', payload);

export const stopActiveBotRunner = async (payload: {
  user_id: string;
  select_id: string;
  title: string;
  username: string;
  userid: string;
}) => stopBotRuntimeAutomation({
  user_id: payload.user_id,
  select_id: payload.select_id,
});

export const fetchActiveRpaTasks = async (params: {
  q?: string;
  perPage?: number;
  page?: number;
  sort?: 'asc' | 'desc';
  sortColumn?: string;
}) => {
  const payload = {
    userId: localStorage.getItem('id') || '',
    role: localStorage.getItem('Role') || '',
    user_type: localStorage.getItem('UserType') || '',
    perPage: params.perPage || 10,
    page: params.page || 1,
    sort: params.sort || 'desc',
    sortColumn: params.sortColumn || 'createdAt',
    q: params.q || '',
  };

  const result = await encryptedPost<{ data?: ActiveBotTask[]; count?: number } | string>('api/rpa-management/get-activities', payload);

  if (typeof result === 'string') {
    return { data: [], count: 0 };
  }

  return {
    data: Array.isArray(result.data) ? result.data : [],
    count: Number(result.count || 0),
  };
};

export const stopActiveRpaTask = async (payload: {
  userId: string;
  username: string;
  taskId: string;
  title: string;
}) => encryptedPost<{ success: boolean; message: string }>('api/rpa-management/stop-bot', payload);

export const stopActiveRpaRunner = async (payload: {
  user_id: string;
  rpa_id: string;
}) => stopRpaRuntimeAutomation({
  user_id: payload.user_id,
  rpa_id: payload.rpa_id,
});

// --- Bot Log Viewer Functions ---

export interface BotLogHistoryItem {
  id: string;
  task_run_id: string;
  effectiveTitle: string;
  user_id?: string;
  username: string;
  task_name: string;
  task_type: string;
  task_start_time: string | number;
  task_completion_time?: string | number;
  agent_run_time?: string;
  task_status?: string;
  download_status?: string;
  model_name?: string;
  total_tokens?: number;
}

export interface BotLogDetailsResponse {
  task_run_id?: string;
  automation_raw_logs?: any[];
  final_logs?: any[];
  error?: string;
}

export const fetchRpaLogHistory = async (params: FetchBotTasksParams) => {
  const payload = {
    userId: localStorage.getItem('id') || '',
    user_type: localStorage.getItem('UserType') || '',
    perPage: params.perPage || 10,
    page: params.page || 1,
    q: params.q || '',
  };

  const result = await encryptedPost<{ data?: BotLogHistoryItem[]; count?: number } | string>('api/rpa/logs/history', payload);
  if (typeof result === 'string') {
    return { data: [], count: 0 };
  }

  return {
    data: Array.isArray(result.data) ? result.data : [],
    count: Number(result.count || 0),
  };
};

export const fetchIpaLogHistory = async (params: FetchBotTasksParams) => {
  const payload = {
    userId: localStorage.getItem('id') || '',
    user_type: localStorage.getItem('UserType') || '',
    perPage: params.perPage || 10,
    page: params.page || 1,
    q: params.q || '',
  };

  const result = await encryptedPost<{ data?: BotLogHistoryItem[]; count?: number } | string>('api/ipa/logs/history', payload);
  if (typeof result === 'string') {
    return { data: [], count: 0 };
  }

  return {
    data: Array.isArray(result.data) ? result.data : [],
    count: Number(result.count || 0),
  };
};

export const fetchRpaLogDetails = async (task_run_id: string) => {
  const result = await encryptedPost<BotLogDetailsResponse>(`api/rpa/logs/${task_run_id}`, { task_run_id });
  return typeof result === 'object' && result !== null ? result : { automation_raw_logs: [] };
};

export const fetchIpaLogDetails = async (task_run_id: string) => {
  const result = await encryptedPost<BotLogDetailsResponse>(`api/ipa/logs/${task_run_id}`, { task_run_id });
  return typeof result === 'object' && result !== null ? result : { automation_raw_logs: [], final_logs: [] };
};
